import React, { useEffect, useState } from "react";
import translate from "../../i18n/translate";
import { connect } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import "./sidebar.css";
import { currentRates, getTopNumbersPerSeller } from "../../actions/currencyAction";
import _ from "lodash";
import Toolbar from "@mui/material/Toolbar";
import PhoneIcon from "@mui/icons-material/Phone";
import EditIcon from "@mui/icons-material/Edit";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";

import { Blink } from "@bdchauvette/react-blink";
import { dataLanguage } from "../../i18n/translateRequest";

const RightSideBar = ({ user, userData, isDarktheme }) => {
  const [rates, setRates] = useState([]);
  const [topNumbers, setTopNumbers] = useState([]);
  const [loading, isLoading] = useState(false);
  const screenWidth = window.innerWidth;
  const location = useLocation();
  const history = useHistory();
  
  // Customer name management
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedNumber, setSelectedNumber] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerNames, setCustomerNames] = useState({});
  
  // Track current phone number input for highlighting
  const [currentPhoneInput, setCurrentPhoneInput] = useState("");
  
  // Check if current page is for a specific provider
  const isJawwalPage = location.pathname.toLowerCase().includes('jawwal');
  const isOoredooPage = location.pathname.toLowerCase().includes('ooredoo');
  const isCellcomPage = location.pathname.toLowerCase().includes('cellcom');
  const isPelephonePage = location.pathname.toLowerCase().includes('pelephone');
  const isPartnerPage = location.pathname.toLowerCase().includes('partner');
  const isGolanPage = location.pathname.toLowerCase().includes('golan');
  const isAzyPage = location.pathname.toLowerCase().includes('azy');
  const is012MobilePage = location.pathname.toLowerCase().includes('012mobile') || location.pathname.toLowerCase().includes('012 mobile');
  const isHotPage = location.pathname.toLowerCase().includes('/hot');
  const isBillPage = location.pathname.toLowerCase().includes('/pay_bills/');
  
  const isTopNumbersPage = isJawwalPage || isOoredooPage || isCellcomPage || isPelephonePage || 
                           isPartnerPage || isGolanPage || isAzyPage || is012MobilePage || isHotPage || isBillPage;
  
  // Determine current provider
  const getCurrentProvider = () => {
    const path = location.pathname.toLowerCase();
    
    // Check for bill pages first (more specific routes)
    if (path.includes('/pay_bills/jawwal')) return 'Jawwal Invoice';
    if (path.includes('/pay_bills/ooredoo')) return 'Ooredoo Invoice';
    if (path.includes('/pay_bills/paltel')) return 'Paltel';
    if (path.includes('/pay_bills/mada')) return 'Mada';
    if (path.includes('/pay_bills/nedco')) return 'Nedco';
    if (path.includes('/pay_bills/selco')) return 'Selco';
    if (path.includes('/pay_bills/coolnet')) return 'Coolnet';
    
    // Check for prepaid/bundle pages
    if (isJawwalPage) return 'jawwal';
    if (isOoredooPage) return 'ooredoo';
    if (isCellcomPage) return 'cellcom';
    if (isPelephonePage) return 'pelephone';
    if (isPartnerPage) return 'partner';
    if (isGolanPage) return 'golan';
    if (isAzyPage) return 'azy';
    if (is012MobilePage) return '012mobile';
    if (isHotPage) return 'hot';
    return 'jawwal'; // default
  };
  
  const currentProvider = getCurrentProvider();

  // Load customer names from localStorage on mount
  useEffect(() => {
    const savedNames = localStorage.getItem('phoneplay_customer_names');
    if (savedNames) {
      try {
        setCustomerNames(JSON.parse(savedNames));
      } catch (e) {
        console.error('Error loading customer names:', e);
      }
    }
  }, []);

  // Listen for phone number input changes from provider pages
  useEffect(() => {
    const handlePhoneInputChange = (event) => {
      const phoneNumber = event.detail?.phoneNumber || "";
      console.log('📱 Phone input event received:', phoneNumber);
      setCurrentPhoneInput(phoneNumber);
    };

    window.addEventListener('phoneNumberInputChange', handlePhoneInputChange);
    console.log('👂 Phone input event listener registered');

    return () => {
      window.removeEventListener('phoneNumberInputChange', handlePhoneInputChange);
    };
  }, []);

  // Extract phone number from URL for providers that use URL routing
  useEffect(() => {
    // For providers like Ooredoo, Cellcom, Pelephone, Partner, Golan, Azy, 012 Mobile, Hot
    // The URL format is: /company/provider/bundleType/phoneNumber or /group/provider/phoneNumber
    const pathParts = location.pathname.split('/').filter(part => part); // Remove empty parts
    
    console.log('🔍 URL Analysis:', {
      pathname: location.pathname,
      pathParts,
      isTopNumbersPage,
      isOoredooPage,
      currentProvider,
      pathLength: pathParts.length
    });
    
    // Check if we're on a provider page with phone number in URL
    if (isTopNumbersPage && pathParts.length >= 3) {
      // Get the last part of the URL which should be the phone number
      const lastPart = pathParts[pathParts.length - 1];
      
      console.log('🔍 Checking last part:', lastPart, 'Is valid phone?', /^\d{10}$/.test(lastPart));
      
      // Check if it's a valid 10-digit phone number
      if (/^\d{10}$/.test(lastPart)) {
        setCurrentPhoneInput(lastPart);
        console.log('✅ Phone number extracted from URL:', lastPart, 'Provider:', currentProvider);
      } else {
        // If not on a page with phone number, clear the highlight
        console.log('❌ No valid phone number in URL, clearing highlight');
        setCurrentPhoneInput("");
      }
    } else if (!isTopNumbersPage) {
      // Clear highlight when not on a provider page
      console.log('❌ Not on provider page, clearing highlight');
      setCurrentPhoneInput("");
    }
  }, [location.pathname, isTopNumbersPage, isOoredooPage, currentProvider]);

  // Save customer names to localStorage whenever they change
  const saveCustomerName = (number, name) => {
    const updatedNames = { ...customerNames };
    if (name && name.trim()) {
      updatedNames[number] = name.trim();
    } else {
      delete updatedNames[number];
    }
    setCustomerNames(updatedNames);
    localStorage.setItem('phoneplay_customer_names', JSON.stringify(updatedNames));
  };

  const handleEditClick = (e, number) => {
    e.stopPropagation(); // Prevent triggering the row click
    setSelectedNumber(number);
    setCustomerName(customerNames[number] || "");
    setEditModalOpen(true);
  };

  const handleSaveCustomerName = () => {
    saveCustomerName(selectedNumber, customerName);
    setEditModalOpen(false);
    setSelectedNumber("");
    setCustomerName("");
  };

  const handleCloseModal = () => {
    setEditModalOpen(false);
    setSelectedNumber("");
    setCustomerName("");
  };

  useEffect(() => {
    if (isTopNumbersPage) {
      updateTopNumbers();
    } else {
      updateCurrencyRate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTopNumbersPage, currentProvider]);

  const updateCurrencyRate = () => {
    isLoading(true);
    currentRates().then((rates) => {
      rates = [
        rates[0],
        rates[1],
        rates[2],
        rates[6],
        rates[7],
        rates[3],
        rates[4],
        rates[5],
      ];
      setRates(rates);
      isLoading(false);
    });
  };

  const updateTopNumbers = () => {
    isLoading(true);
    getTopNumbersPerSeller(currentProvider).then((numbers) => {
      setTopNumbers(numbers);
      isLoading(false);
    });
  };

  const handleNumberClick = (number) => {
    console.log('=== Top 20 Number Click Debug ===');
    console.log('Number clicked:', number);
    console.log('Number type:', typeof number);
    console.log('Number length:', number?.length);
    console.log('Provider:', currentProvider);
    
    // Validate number format based on provider
    if (!number) {
      console.error('Invalid number: empty or null');
      alert('Invalid number: empty or null');
      return;
    }
    
    // Clean the number: remove spaces, dashes, and keep only digits
    const cleanedNumber = number.toString().replace(/[\s\-]/g, '').trim();
    console.log('Cleaned number:', cleanedNumber);
    console.log('Cleaned number length:', cleanedNumber.length);
    
    // Different providers have different number formats
    const numberLength = cleanedNumber.length;
    const billProviders = ['Jawwal Invoice', 'Ooredoo Invoice', 'Paltel', 'Mada', 'Nedco', 'Selco', 'Coolnet'];
    
    // Validate based on provider type with specific rules
    if (currentProvider === 'Paltel') {
      // Paltel: 5+ digits (landline numbers)
      if (numberLength < 5) {
        console.error('Invalid Paltel number format:', cleanedNumber);
        alert(`Invalid number format: ${cleanedNumber}\nExpected 5+ digits for Paltel, got: ${numberLength}`);
        return;
      }
    } else if (currentProvider === 'Mada') {
      // Mada: 8+ digits (service numbers)
      if (numberLength < 8) {
        console.error('Invalid Mada number format:', cleanedNumber);
        alert(`Invalid number format: ${cleanedNumber}\nExpected 8+ digits for Mada, got: ${numberLength}`);
        return;
      }
    } else if (currentProvider === 'Nedco' || currentProvider === 'Selco' || currentProvider === 'Coolnet') {
      // Electric companies: Very flexible validation for meter numbers (they can vary)
      // Just check that we have at least some digits
      if (numberLength < 1) {
        console.error('Invalid meter number format:', cleanedNumber);
        alert(`Invalid number format: ${cleanedNumber}\nMeter number cannot be empty`);
        return;
      }
      console.log(`Electric company meter number accepted: ${cleanedNumber} (${numberLength} digits)`);
    } else {
      // Mobile numbers (Jawwal, Ooredoo prepaid and bills): 10 digits
      if (numberLength !== 10) {
        console.error('Invalid mobile number format:', cleanedNumber);
        alert(`Invalid number format: ${cleanedNumber}\nExpected 10 digits, got: ${numberLength}`);
        return;
      }
    }
    
    // Check if we're on a bill page
    if (billProviders.includes(currentProvider)) {
      // For bill pages, dispatch a custom event with the cleaned phone number
      console.log('Bill page detected - dispatching custom event with number:', cleanedNumber);
      
      // Dispatch custom event that bill pages can listen to
      const event = new CustomEvent('fillBillPhoneNumber', {
        detail: { phoneNumber: cleanedNumber },
        bubbles: true
      });
      window.dispatchEvent(event);
      
      console.log('Custom event dispatched: fillBillPhoneNumber with number:', cleanedNumber);
      console.log('=== End Debug ===');
      return;
    }
    
    // For prepaid pages, navigate to bundle page
    console.log('Prepaid page detected - navigating to bundle page');
    
    // Clear previous session data for all providers
    console.log('Clearing session storage...');
    // Palestinian providers
    sessionStorage.removeItem("JawwalMin");
    sessionStorage.removeItem("Jawwal3g");
    sessionStorage.removeItem("JawwalCredit");
    sessionStorage.removeItem("JawwalCreditQueue");
    sessionStorage.removeItem("JawwalRom");
    sessionStorage.removeItem("JawwalPlus");
    sessionStorage.removeItem("ooredooCredit");
    sessionStorage.removeItem("ooredooMin");
    sessionStorage.removeItem("ooredoo3g");
    sessionStorage.removeItem("ooredooRom");
    sessionStorage.removeItem("ooredooShabab");
    // Israeli providers
    sessionStorage.removeItem("cellcomCredit");
    sessionStorage.removeItem("pelephoneCredit");
    sessionStorage.removeItem("partnerCredit");
    sessionStorage.removeItem("golanCredit");
    sessionStorage.removeItem("azyCredit");
    sessionStorage.removeItem("012mobileCredit");
    sessionStorage.removeItem("hot");
    
    // Map provider to credit page route
    const providerRoutes = {
      'jawwal': 'jawwalCredit',
      'ooredoo': 'ooredoo/credit',
      'cellcom': 'cellcomCredit',
      'pelephone': 'pelephoneCredit',
      'partner': 'partnerCredit',
      'golan': 'golanCredit',
      'azy': 'azyCredit',
      '012mobile': 'group/012 mobile',
      'hot': 'hot'
    };
    
    const routeName = providerRoutes[currentProvider] || 'jawwalCredit';
    const targetUrl = `/company/${routeName}/${cleanedNumber}`;
    
    console.log('Target URL:', targetUrl);
    console.log('Current location:', history.location.pathname);
    
    try {
      history.push(targetUrl);
      console.log('Navigation push called successfully');
      console.log('New location should be:', targetUrl);
    } catch (error) {
      console.error('Navigation error:', error);
      alert(`Navigation failed: ${error.message}`);
    }
    
    console.log('=== End Debug ===');
  };

  return (
    <div className="col-lg-2 col-md-3 col-sm-6">
      <div className="card card-currency">
        <div className={!isDarktheme ? "card-body" : "dark-card-body"}>
          {!loading && !isTopNumbersPage && (
            <>
              <h6
                style={{
                  fontSize: ".8rem",
                }}
              >
                {translate("Currency exchange rates")}
              </h6>
              <hr className="divider my-1"></hr>
              <table className="currecy-rates mt-2">
                <thead>
                  <tr>
                    <th
                      style={{
                        fontSize: ".7rem",
                      }}
                    >
                      {translate("Currency")}
                    </th>
                    <th
                      style={{
                        fontSize: ".7rem",
                      }}
                    >
                      {translate("Price")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rates.map((rate, index) => (
                    <tr key={index.toString()}>
                      <td>
                        <Toolbar
                          title={dataLanguage(
                            rate,
                            "ar_des",
                            "en_des",
                            "he_des"
                          )}
                          style={{
                            minHeight: "auto",
                            padding: "0",
                            whiteSpace: "nowrap",
                            display: "flex",
                            justifyContent: "center",
                            fontSize: ".7rem",
                          }}
                        >
                          {index > 4
                            ? translate(_.trim(rate.currency) + " / USD")
                            : translate(_.trim(rate.currency) + " / ILS")}
                        </Toolbar>
                      </td>
                      <td style={{ fontSize: ".7rem" }}>{rate.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          {!loading && isTopNumbersPage && (
            <>
              {/* Provider Logo */}
              <div style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginBottom: "15px",
                marginTop: "10px",
                padding: "10px",
                minHeight: "60px"
              }}>
                {(() => {
                  const providerLogos = {
                    'jawwal': {
                      url: 'https://images.celltop.vip/logo_location/jawwal.png',
                      name: 'Jawwal',
                      color: '#00a651'
                    },
                    'ooredoo': {
                      url: 'https://images.celltop.vip/logo_location/ooredoo.png',
                      name: 'Ooredoo',
                      color: '#e30613'
                    },
                    'cellcom': {
                      url: 'https://images.celltop.vip/logo_location/cellcom.png',
                      name: 'Cellcom',
                      color: '#0066cc'
                    },
                    'pelephone': {
                      url: 'https://images.celltop.vip/logo_location/pelephone.png',
                      name: 'Pelephone',
                      color: '#ff6600'
                    },
                    'partner': {
                      url: 'https://images.celltop.vip/logo_location/partner.png',
                      name: 'Partner',
                      color: '#ff7700'
                    },
                    'golan': {
                      url: 'https://images.celltop.vip/logo_location/golan.png',
                      name: 'Golan',
                      color: '#0099cc'
                    },
                    'azy': {
                      url: 'https://images.celltop.vip/logo_location/azy.png',
                      name: 'Azy',
                      color: '#cc0000'
                    },
                    '012mobile': {
                      url: 'https://images.celltop.vip/logo_location/012mobile.png',
                      name: '012 Mobile',
                      color: '#ff0066'
                    },
                    'hot': {
                      url: 'https://images.celltop.vip/CARDS/HOTMOBILE.PNG',
                      name: 'Hot Mobile',
                      color: '#ff0033'
                    }
                  };
                  
                  const providerInfo = providerLogos[currentProvider] || providerLogos['jawwal'];
                  
                  return (
                    <img 
                      src={providerInfo.url}
                      alt={providerInfo.name}
                      style={{
                        width: "100px",
                        height: "auto",
                        maxHeight: "50px",
                        objectFit: "contain"
                      }}
                      onError={(e) => {
                        // Fallback if image fails to load
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML += `<span style="font-weight: bold; color: ${providerInfo.color};">${providerInfo.name}</span>`;
                      }}
                    />
                  );
                })()}
              </div>
              <h6
                style={{
                  fontSize: ".8rem",
                }}
              >
                {translate("Top 20 Numbers")}
              </h6>
              <hr className="divider my-1"></hr>
              <table className="currecy-rates mt-2" style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th
                      style={{
                        fontSize: ".7rem",
                      }}
                    >
                      {translate("Number")}
                    </th>
                    <th
                      style={{
                        fontSize: ".7rem",
                      }}
                    >
                      {translate("Count")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {topNumbers.length > 0 ? (
                    topNumbers.map((item, index) => (
                      <tr key={index.toString()}>
                        <td 
                          style={{ 
                            fontSize: ".7rem", 
                            textAlign: "center",
                            cursor: "pointer",
                            padding: "4px 8px"
                          }}
                          onClick={() => handleNumberClick(item.number)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = "0.7";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = "1";
                          }}
                          title="Click to use this number"
                        >
                          <div style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "2px"
                          }}>
                            <div style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "4px",
                              color: currentPhoneInput === item.number ? "#00a651" : (isDarktheme ? "#4fc3f7" : "#1976d2"),
                              textDecoration: "underline",
                              fontWeight: currentPhoneInput === item.number ? "700" : "500",
                              transition: "all 0.3s ease"
                            }}>
                              <PhoneIcon 
                                style={{ 
                                  fontSize: "0.9rem", 
                                  color: currentPhoneInput === item.number ? "#00a651" : "#00a651",
                                  marginRight: "2px"
                                }} 
                              />
                              {item.number}
                              <EditIcon
                                style={{
                                  fontSize: "0.8rem",
                                  color: currentPhoneInput === item.number ? "#00a651" : (isDarktheme ? "#90caf9" : "#666"),
                                  marginLeft: "4px",
                                  cursor: "pointer"
                                }}
                                onClick={(e) => handleEditClick(e, item.number)}
                                title="Edit customer name"
                              />
                            </div>
                            {customerNames[item.number] && (
                              <div style={{
                                fontSize: "0.65rem",
                                color: isDarktheme ? "#aaa" : "#666",
                                fontStyle: "italic",
                                marginTop: "2px"
                              }}>
                                {customerNames[item.number]}
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={{ fontSize: ".7rem", textAlign: "center", padding: "4px 8px" }}>
                          {item.count}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="2" style={{ fontSize: ".7rem", textAlign: "center", padding: "10px" }}>
                        {translate("No data available")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </>
          )}
          {loading && (
            <div>
              <Blink>
                <div
                  style={{
                    height: 5,
                    width: 5,
                    borderWidth: 1,
                    borderRadius: 50,
                    borderColor: "red",
                    backgroundColor: "red",
                    borderStyle: "solid",
                  }}
                />
              </Blink>
            </div>
          )}
        </div>
      </div>
      {screenWidth > 768 && !isTopNumbersPage && (
        <div
          className="add-banner"
          style={{
            background: `url("${sessionStorage.getItem("rightImage")}")`,
          }}
        ></div>
      )}
      
      {/* Customer Name Edit Modal */}
      <Dialog 
        open={editModalOpen} 
        onClose={handleCloseModal}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {customerNames[selectedNumber] ? "Edit Customer Name" : "Add Customer Name"}
        </DialogTitle>
        <DialogContent>
          <div style={{ marginTop: "10px" }}>
            <div style={{ 
              fontSize: "0.9rem", 
              marginBottom: "10px",
              color: isDarktheme ? "#90caf9" : "#1976d2",
              fontWeight: "500"
            }}>
              {selectedNumber}
            </div>
            <TextField
              autoFocus
              fullWidth
              label="Customer Name"
              variant="outlined"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSaveCustomerName();
                }
              }}
              placeholder="Enter customer name..."
              size="small"
            />
          </div>
        </DialogContent>
        <DialogActions>
          {customerNames[selectedNumber] && (
            <Button 
              onClick={() => {
                saveCustomerName(selectedNumber, "");
                handleCloseModal();
              }}
              color="error"
            >
              Remove
            </Button>
          )}
          <Button onClick={handleCloseModal} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleSaveCustomerName} color="primary" variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const mapStateToProps = ({
  auth: { user, userData },
  darkTheme: { isDarktheme },
}) => ({
  user,
  userData,
  isDarktheme,
});

export default connect(mapStateToProps, {})(RightSideBar);
